import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageShell, EmptyState, ErrorState, LoadingPage } from '@resource-kit/ui/react';
import { ApiError } from '@lib/api';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useToast } from '@components/ui/Toast';
import { usePermissions } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import {
  ANALYTICS_QUERY_KEYS,
  deleteAnalyticsReport,
  listAnalyticsReports,
  readAnalyticsReport,
} from './analyticsApi';
import { formatDateTime } from './analyticsFormat';
import { ANALYTICS_FRESHNESS, selectAnalyticsFreshness } from './analyticsState';
import type { AnalyticsReportArchive } from './analyticsTypes';
import { CustomerHealth } from './CustomerHealth';
import { Disclosure } from './Disclosure';
import { ExecutiveSummary } from './ExecutiveSummary';
import { GenerateReportDialog } from './GenerateReportDialog';
import { MetricsPanel } from './MetricsPanel';
import { ReportHeader } from './ReportHeader';
import { ReportList, type LabelledReport } from './ReportList';
import { formatReportLabel } from './reportLabel';

function SyncNotice({ title, description }: { title: string; description: string }) {
  return (
    <div
      role="alert"
      className="flex gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
    >
      <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const toast = useToast();
  const { confirm } = useConfirm();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const [chosenReportId, setChosenReportId] = useState<string | null>(null);
  const canGenerate = permissions.includes(PERMISSIONS.ANALYTICS_GENERATE);
  const archive = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.archive,
    queryFn: listAnalyticsReports,
  });
  const reports: LabelledReport[] = [...(archive.data?.items ?? [])]
    .sort(
      (left, right) =>
        right.period_end.localeCompare(left.period_end) ||
        right.generated_at.localeCompare(left.generated_at) ||
        right.id.localeCompare(left.id),
    )
    .map((summary) => ({ summary, label: formatReportLabel(summary, t, locale) }));
  // A chosen report missing from a settled archive is gone: fall back to the newest.
  const chosenIsLive =
    chosenReportId !== null &&
    (archive.isFetching || reports.some((report) => report.summary.id === chosenReportId));
  const selectedReportId = chosenIsLive ? chosenReportId : (reports[0]?.summary.id ?? null);
  const report = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.detail(selectedReportId ?? ''),
    queryFn: () => readAnalyticsReport(selectedReportId ?? ''),
    enabled: selectedReportId !== null,
  });
  const freshness =
    archive.data === undefined ? undefined : selectAnalyticsFreshness(archive.data.sync);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      try {
        await deleteAnalyticsReport(id);
      } catch (error) {
        // 404 = already gone (another tab or an earlier request): same end state.
        if (!(error instanceof ApiError && error.status === 404)) {
          throw error;
        }
      }
    },
    onSuccess: (_, id) => {
      if (id === selectedReportId) {
        setChosenReportId(null);
      }
      // Drop the row now so the fallback selection never points at the deleted id.
      queryClient.setQueryData<AnalyticsReportArchive>(ANALYTICS_QUERY_KEYS.archive, (archive) =>
        archive === undefined
          ? archive
          : { ...archive, items: archive.items.filter((item) => item.id !== id) },
      );
      queryClient.removeQueries({ queryKey: ANALYTICS_QUERY_KEYS.detail(id), exact: true });
      void queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEYS.archive, exact: true });
      toast.success(t('analytics.delete.successTitle'));
    },
    onError: () => {
      toast.error(t('analytics.delete.errorTitle'), t('analytics.delete.errorMessage'));
    },
  });

  async function requestDelete(target: LabelledReport) {
    const confirmed = await confirm({
      title: t('analytics.delete.confirmTitle'),
      message: t('analytics.delete.confirmMessage', { label: target.label }),
      type: 'danger',
      confirmText: t('analytics.delete.button'),
      confirmTestId: 'analytics-delete-confirm',
    });
    if (confirmed) {
      remove.mutate(target.summary.id);
    }
  }

  if (archive.isPending) {
    return <LoadingPage message={t('analytics.archive.loading')} />;
  }
  if (archive.isError || archive.data === undefined) {
    return <ErrorState message={t('analytics.archive.error')} />;
  }

  const onDelete = canGenerate ? requestDelete : undefined;
  const selected = reports.find((item) => item.summary.id === selectedReportId);

  return (
    <PageShell
      title={t('analytics.page.title')}
      subtitle={t('analytics.page.subtitle')}
      actions={
        canGenerate ? (
          <GenerateReportDialog freshness={freshness} onGenerated={setChosenReportId} />
        ) : undefined
      }
    >
      <div className="flex min-w-0 flex-col gap-6">
        {freshness?.kind === ANALYTICS_FRESHNESS.stale && (
          <SyncNotice
            title={t('analytics.sync.staleTitle')}
            description={t('analytics.sync.staleDescription', {
              time: formatDateTime(freshness.lastSuccessfulSyncAt, locale),
            })}
          />
        )}
        {freshness?.kind === ANALYTICS_FRESHNESS.unavailable && (
          <SyncNotice
            title={t('analytics.sync.unavailableTitle')}
            description={t('analytics.sync.unavailableDescription')}
          />
        )}

        {reports.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title={t('analytics.archive.emptyTitle')}
            description={t('analytics.archive.emptyDescription')}
          />
        ) : (
          <div className="grid min-w-0 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-start lg:gap-10">
            <ReportList
              reports={reports}
              selectedId={selectedReportId}
              onSelect={setChosenReportId}
              onDelete={onDelete}
              deleting={remove.isPending}
            />

            <div className="flex min-w-0 flex-col gap-10">
              {report.isPending ? (
                <LoadingPage message={t('analytics.report.loading')} />
              ) : report.isError || report.data === undefined ? (
                <ErrorState message={t('analytics.report.error')} />
              ) : (
                <>
                  <ReportHeader
                    summary={report.data}
                    label={selected?.label ?? formatReportLabel(report.data, t, locale)}
                    freshness={freshness}
                    onDelete={onDelete && selected ? () => void onDelete(selected) : undefined}
                    deleting={remove.isPending}
                  />
                  <ExecutiveSummary
                    metrics={report.data.metrics}
                    periodStart={report.data.period_start}
                    periodEnd={report.data.period_end}
                  />
                  <CustomerHealth
                    metrics={report.data.metrics}
                    generatedAt={report.data.generated_at}
                  />
                  <div className="flex flex-col gap-1 border-t pt-4">
                    <Disclosure title={t('analytics.details.title')}>
                      <MetricsPanel metrics={report.data.metrics} />
                    </Disclosure>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
